import { BoxButton, Divider, Svg, Table, TableCell, List, StickyBottomWrapper, semanticColor } from '@repo/ui';

import ApplyResultInfoView from '@/components/tax-refund/common/view/ApplyResultInfoView';
import { formatAccountBankNo, formatDate } from '@/lib/utils/stringFormat';
import { zCurrency } from '@/lib/utils/zCurrency';

export const TritxApplyPresentationalContent = ({
  data,
  handleCardClick,
  handleClickCancel,
  handleRevokeClick
}: {
  data: any;
  handleCardClick: () => void;
  handleClickCancel: () => void;
  handleRevokeClick: () => Promise<void>;
}) => {
  const { tritxRefundInfo, disabledCancelButton, disabledRevokeButton, isRefundCompleted } = data;
  const { hasRegisteredCard, cardName, cardLogo, cardNumber } = data.paymentCard;
  const infoContents = [
    {
      content: '환급액은 제휴 세무사와 국세청 검토 후 결정되며, 검토 과정에서 일부 변동될 수 있어요.'
    },
    {
      content: hasRegisteredCard ? '환급이 완료되면 등록된 결제 카드로 이용료가 자동 결제돼요.' : '환급이 완료되면 세무 이용료를 결제 요청드려요.'
    }
  ];

  return (
    <div>
      <div className={'px-4.5 py-tiny-large'}>
        <div className={'pb-normal-large'}>
          <div className={'body-large-regular text-neutral-foreground-low pt-normal-large'}>양도소득세 예상 환급액</div>
          <div className={'text-neutral-foreground-main heading-medium-bold'}>{zCurrency(tritxRefundInfo?.refundAmount ?? 0, '원', true, '0')}</div>
          <Divider className={'my-normal-medium'} />
          {tritxRefundInfo?.applicationDt && (
            <Table
              headerCell={<TableCell variant={'header'}>환급 신청일</TableCell>}
              contentCell={<TableCell className={'flex-1 text-right'}>{formatDate(tritxRefundInfo?.applicationDt ?? null)}</TableCell>}
            />
          )}

          <Table
            headerCell={<TableCell variant={'header'}>예상 환급일</TableCell>}
            contentCell={<TableCell className={'flex-1 text-right'}>신청일부터 2~3개월</TableCell>}
          />
          {tritxRefundInfo?.applicationInfo?.accountNo && (
            <Table
              headerCell={<TableCell variant={'header'}>환급 계좌</TableCell>}
              contentCell={
                <TableCell className={'flex-1 text-right'}>
                  {formatAccountBankNo(tritxRefundInfo?.applicationInfo?.bank, tritxRefundInfo?.applicationInfo?.accountNo)}
                </TableCell>
              }
            />
          )}
          <Table
            headerCell={
              <TableCell variant={'header'} className={'flex items-center'}>
                이용료
              </TableCell>
            }
            contentCell={
              <TableCell className={'flex-1 text-right'}>
                <div>
                  <p className="mb-1">결정 환급액의 {tritxRefundInfo?.partnerInfo?.defaultFeeRate ?? '30'}%</p>
                  <p className="text-13 text-gray-500">(부가세 별도)</p>
                </div>
              </TableCell>
            }
          />
          {(tritxRefundInfo?.partnerInfo?.discountRate ?? '0') !== '0' && (
            <Table
              headerCell={
                <TableCell variant={'header'} className={'flex items-center'}>
                  이용료 할인
                </TableCell>
              }
              contentCell={
                <TableCell className={'flex-1 text-right'}>
                  <div>
                    <p className="mb-1">제휴 할인 {tritxRefundInfo?.partnerInfo?.discountRate}% 적용</p>
                    <p className="text-13 text-gray-500">
                      결정 환급액의 <span className="line-through">{tritxRefundInfo?.partnerInfo?.defaultFeeRate}%</span>-&gt;
                      {tritxRefundInfo?.partnerInfo?.feeRate}
                      %(vat별도)
                    </p>
                  </div>
                </TableCell>
              }
            />
          )}
          {hasRegisteredCard && (
            <Table
              headerCell={
                <TableCell className={'flex items-center'} variant={'header'}>
                  결제 카드
                </TableCell>
              }
              contentCell={
                <>
                  <TableCell className={'flex items-center flex-1 justify-end'}>
                    <div className={'flex gap-tiny-medium text-[15px]'}>
                      <Svg src={cardLogo ?? ''} width={24} height={24} />
                      {cardName}({cardNumber.slice(-4)})
                    </div>
                  </TableCell>
                  <TableCell>
                    <BoxButton size={'small'} className={'ml-3'} variant={'tonal-secondary'} onClick={handleCardClick}>
                      변경
                    </BoxButton>
                  </TableCell>
                </>
              }
            />
          )}
        </div>
        <ApplyResultInfoView data={infoContents} />
        {tritxRefundInfo?.cancelStage !== 'Canceling' &&
          tritxRefundInfo?.refundStatus !== 'RefundImpossible' &&
          (['Review', 'Declare'].includes(tritxRefundInfo?.refundStage ?? '') || isRefundCompleted) && (
            <>
              <Divider />
              <div className={'pt-tiny-large'}>
                <List
                  title={isRefundCompleted ? '환급이 결정되어 취소할 수 없어요' : ''}
                  size={'small'}
                  primaryAction={!isRefundCompleted}
                  onClick={handleClickCancel}
                  state={disabledCancelButton ? 'disabled' : 'enabled'}
                  supportingElement={
                    <div
                      className={`flex items-center ${disabledCancelButton ? 'text-neutral-foreground-disabled' : 'text-primary-foreground-main'} body-small-medium`}
                    >
                      {isRefundCompleted ? '' : '양도소득세 '}신청 취소
                      <Svg
                        src={'ic_chevron-right.svg'}
                        width={18}
                        height={18}
                        color={disabledCancelButton ? semanticColor.neutral.foreground.disabled : semanticColor.primary.foreground.main}
                      />
                    </div>
                  }
                />
              </div>
            </>
          )}
        {tritxRefundInfo?.cancelStage === 'Canceling' && (
          <StickyBottomWrapper>
            <BoxButton className={'w-full'} size={'large'} variant={'tonal-primary'} onClick={handleRevokeClick} disabled={disabledRevokeButton}>
              다시 환급 진행하기
            </BoxButton>
          </StickyBottomWrapper>
        )}
      </div>
    </div>
  );
};
